import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { mapParamsBySchema } from '../../../util';
import Global from '../../../Services/Common/Global';
import { apiRequest } from '../../../Services/Common/ApiRequest';
import Commons from '../../../Services/Common/Common';
import { M_PEA_EDU_LOG_DETAIL_Schema } from './M_PEA_EDU_LOG_DETAIL_Schema';
import {
  ParseTraCustom02R1Rows,
  ParseTraCustom02R2Rows,
  SearchLogicPeaEduLogDetail1,
} from './M_PEA_EDU_LOG_DETAIL_Logic';

export const useSearchPeaEduLogDetail1 = (tpmId, listRow) => {
  const { userInfo } = useSelector((s) => s.BaseInfo);
  const searchInfo = useSelector((s) => s.searchInfo);
  const effectiveCustCd = userInfo?.custCd || searchInfo?.custCd || '';
  const effectiveUserId = userInfo?.userid ?? '';

  const range = useMemo(() => {
    const reqDt = listRow?.reqDt ?? listRow?.ReqDt;
    if (reqDt) {
      const d = String(reqDt).substring(0, 10);
      return { startDtPlain: d, endDtPlain: d };
    }
    const end = dayjs().format('YYYY-MM-DD');
    const start = dayjs().subtract(3, 'year').format('YYYY-MM-DD');
    return { startDtPlain: start, endDtPlain: end };
  }, [listRow]);

  /* mapParamsBySchema가 Store의 userInfo/search를 읽음 — custCd 반영 위해 재계산 */
  const params = useMemo(
    () =>
      mapParamsBySchema(M_PEA_EDU_LOG_DETAIL_Schema.mapper.getTraCustom02R1, {
        startDtPlain: range.startDtPlain,
        endDtPlain: range.endDtPlain,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- CustCd/UserId 반영
    [range.startDtPlain, range.endDtPlain, effectiveCustCd, effectiveUserId]
  );

  const queryEnabled = Boolean(tpmId && effectiveCustCd && effectiveUserId);

  const mainQuery = useQuery({
    queryKey: ['PeaEduLogDetail', 'GetTraCustom02R1', params, tpmId],
    queryFn: async () => {
      console.log('[PeaEduLog][GetTraCustom02R1] queryFn 호출', {
        tpmId,
        effectiveCustCd: effectiveCustCd ? '(있음)' : '(없음)',
        userId: effectiveUserId ? '(있음)' : '(없음)',
      });
      try {
        const res = await apiRequest(
          'post',
          Global.urls.DocsManagement.getTraCustom02R1,
          params
        );
        console.log('[PeaEduLog][GetTraCustom02R1] 응답 원본', res);
        return res;
      } catch (e) {
        console.error('[PeaEduLog][GetTraCustom02R1] 요청 실패', e);
        throw e;
      }
    },
    enabled: queryEnabled,
  });

  const r2Params = useMemo(
    () => ({ TpmId: Commons.EncryptAES(String(tpmId || '')) }),
    [tpmId]
  );

  const attQuery = useQuery({
    queryKey: ['PeaEduLogDetail', 'GetTraCustom02R2', tpmId],
    queryFn: async () => {
      const res = await apiRequest(
        'post',
        Global.urls.DocsManagement.getTraCustom02R2,
        r2Params
      );
      console.log('[PeaEduLog][GetTraCustom02R2] 응답 원본', res);
      return res;
    },
    enabled: queryEnabled,
  });

  const parsedMain = useMemo(() => {
    if (!mainQuery.isSuccess || mainQuery.data === undefined) {
      return { rows: [], errorMessage: null };
    }
    return ParseTraCustom02R1Rows(mainQuery.data);
  }, [mainQuery.isSuccess, mainQuery.data]);

  const apiRow = useMemo(() => {
    if (!parsedMain.rows?.length || !tpmId) return null;
    return parsedMain.rows.find(
      (r) => String(r.tpmId ?? r.TpmId ?? '') === String(tpmId)
    );
  }, [parsedMain.rows, tpmId]);

  const detail = useMemo(() => {
    if (!apiRow) return null;
    const attRows = attQuery.isSuccess
      ? ParseTraCustom02R2Rows(attQuery.data)
      : [];
    return SearchLogicPeaEduLogDetail1(apiRow, listRow, attRows);
  }, [apiRow, listRow, attQuery.isSuccess, attQuery.data]);

  useEffect(() => {
    if (!tpmId) return;
    console.log('[PeaEduLog][상세] location.state 목록행(listRow)', listRow);
  }, [tpmId, listRow]);

  useEffect(() => {
    if (!tpmId) return;
    if (effectiveCustCd && effectiveUserId) return;
    console.warn(
      '[PeaEduLog] GetTraCustom02R1·R2 쿼리 비활성(enabled=false): CustCd 또는 UserId 없음',
      {
        userInfoCustCd: userInfo?.custCd,
        searchCustCd: searchInfo?.custCd,
        userid: userInfo?.userid,
      }
    );
  }, [
    tpmId,
    effectiveCustCd,
    effectiveUserId,
    userInfo?.custCd,
    searchInfo?.custCd,
    userInfo?.userid,
  ]);

  useEffect(() => {
    if (!tpmId || !mainQuery.isSuccess) return;
    console.log(
      '[PeaEduLog][상세] R1 파싱 rows 건수',
      parsedMain.rows?.length,
      parsedMain
    );
    console.log('[PeaEduLog][상세] TpmId 매칭 마스터행(apiRow)', apiRow);
  }, [tpmId, mainQuery.isSuccess, mainQuery.data, parsedMain, apiRow]);

  useEffect(() => {
    if (!tpmId || !attQuery.isSuccess) return;
    const rows = ParseTraCustom02R2Rows(attQuery.data);
    console.log('[PeaEduLog][상세] R2 참석자 파싱 건수', rows.length, rows);
  }, [tpmId, attQuery.isSuccess, attQuery.data]);

  useEffect(() => {
    if (!detail) return;
    console.log('[PeaEduLog][상세] 화면용 detail 모델', detail);
  }, [detail]);

  const notFound =
    mainQuery.isSuccess && !parsedMain.errorMessage && tpmId && !apiRow;

  const errorMessage =
    parsedMain.errorMessage ||
    (mainQuery.isError ? mainQuery.error?.message : null);

  return {
    detail,
    notFound,
    errorMessage,
    isLoading: mainQuery.isLoading,
    isFetching: mainQuery.isFetching || attQuery.isFetching,
    refetchMain: mainQuery.refetch,
    refetchAtt: attQuery.refetch,
  };
};
